import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Bell,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ClipboardList,
  Contact,
  Mail,
  MapPin,
  MoreVertical,
  Phone,
  Plus,
  StickyNote
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';

const palette = {
  bg: '#f6f8fc',
  card: '#ffffff',
  stroke: '#e7edf7',
  title: '#111827',
  text: '#334155',
  muted: '#64748b',
  primary: '#2563eb',
  success: '#16a34a',
  warning: '#f59e0b',
  danger: '#ef4444'
};

const pageFont = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const tabs = [
  { value: 'attivita', label: 'Attivita', icon: ClipboardList },
  { value: 'task', label: 'Task', icon: CheckSquare },
  { value: 'note', label: 'Note', icon: StickyNote },
  { value: 'solleciti', label: 'Solleciti', icon: Bell },
  { value: 'contatti', label: 'Contatti', icon: Contact }
];
const validTabValues = new Set(tabs.map((t) => t.value));

const cardSx = {
  borderRadius: 2.5,
  border: `1px solid ${palette.stroke}`,
  backgroundColor: palette.card,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const tabScrollableSx = {
  maxHeight: { xs: 340, md: 420, lg: 500 },
  overflowY: 'auto',
  pr: 0.5,
  '&::-webkit-scrollbar': { width: 8 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(palette.primary, 0.25),
    borderRadius: 8
  },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const statusToColor = (status) => {
  const lower = (status || '').toLowerCase();
  if (['closed', 'chius', 'complet', 'attivo'].some((k) => lower.includes(k))) return 'success';
  if (['failed', 'terminated', 'errore', 'annull'].some((k) => lower.includes(k))) return 'error';
  if (['transition', 'attesa', 'pending', 'warning', 'lavorazione'].some((k) => lower.includes(k))) return 'warning';
  return 'info';
};

const getInitials = (customer) => {
  const full = `${customer?.name || ''} ${customer?.surname || ''}`.trim();
  if (!full) return 'CL';
  const parts = full.split(' ').filter(Boolean);
  return `${parts[0][0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
};

const getTaskTitle = (task) => task?.titolo || task?.name || (task?.id ? `Task ${task.id}` : 'Task');

const getTaskAssignee = (task) => {
  const full = `${task?.destinatario_name || ''} ${task?.destinatario_surname || ''}`.trim();
  return full || task?.assigned_to_name || '-';
};

const getTaskPriorityLabel = (task) => {
  if (task?.priority || task?.priorita) return task.priority || task.priorita;
  if (String(task?.prioritario) === '1' || task?.prioritario === 1) return 'Alta';
  return 'Media';
};

const decodeHtmlEntities = (value) => {
  if (!value) return '';
  if (typeof window === 'undefined') return value;
  const textarea = window.document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const normalizeNoteText = (value) => {
  if (!value) return '';
  const withLineBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<p[^>]*>/gi, '');
  const decoded = decodeHtmlEntities(withLineBreaks);
  return decoded
    .replace(/<[^>]+>/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const getNoteText = (note) => {
  const raw = note?.descrizione || note?.description || '';
  const normalized = normalizeNoteText(raw);
  return normalized || (note?.id ? `Nota ${note.id}` : 'Nota');
};

const getNoteAuthor = (note) => {
  const full = `${note?.user_name || ''} ${note?.user_surname || ''}`.trim();
  return full || note?.created_by_name || '-';
};

export default function CustomerCumulative() {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchText, setSearchText] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState('attivita');

  const [negotiations, setNegotiations] = useState([]);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);

  const [negotiationTasks, setNegotiationTasks] = useState([]);
  const [negotiationNotes, setNegotiationNotes] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [collapsed, setCollapsed] = useState({
    leftCustomer: false,
    leftDossier: false,
    center: false,
    rightFascicoli: false,
    rightPratiche: false,
    rightRateizzazioni: false,
    rightAllegati: false
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const customerId = params.get('customer_id');
    const tab = params.get('tab');
    if (customerId) setSelectedCustomerId(customerId);
    if (tab && validTabValues.has(tab)) setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingOptions(true);
      try {
        const params = { page: 1, limit: 30, orderBy: 'id', order: 'desc' };
        if (searchText) params.search = searchText;
        const res = await api.get('/customer', { params });
        setCustomerOptions(res.data?.data || []);
      } catch (error) {
        console.error('Error loading customers:', error);
      } finally {
        setLoadingOptions(false);
      }
    };
    const timer = setTimeout(fetchCustomers, 250);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    const fetchAll = async () => {
      setLoadingData(true);
      try {
        const [customerRes, dossierRes, negotiationRes] = await Promise.all([
          api.get(`/customer/${selectedCustomerId}`),
          api.get(`/dossier/customer/${selectedCustomerId}`),
          api.get('/negotiation', {
            params: {
              customer_id: selectedCustomerId,
              page: 1,
              limit: 100,
              orderBy: 'created_at',
              order: 'desc'
            }
          })
        ]);

        const customerData = customerRes.data || null;
        const dossierData = dossierRes.data || [];
        const negotiationData = negotiationRes.data?.data || [];

        setSelectedCustomer(customerData);
        setNegotiations(negotiationData);

        const firstDossier = dossierData[0] || null;
        setSelectedDossier(firstDossier);
        const firstNegotiation = firstDossier
          ? negotiationData.find((n) => String(n.rif_dossier) === String(firstDossier.id)) || negotiationData[0] || null
          : negotiationData[0] || null;
        setSelectedNegotiation(firstNegotiation);
      } catch (error) {
        console.error('Error loading cumulative customer data:', error);
        toast.error('Errore nel caricamento della vista cumulativa cliente');
      } finally {
        setLoadingData(false);
      }
    };
    fetchAll();
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!selectedNegotiation?.id) {
      setNegotiationTasks([]);
      setNegotiationNotes([]);
      return;
    }
    const fetchNegotiationDetails = async () => {
      setLoadingDetails(true);
      try {
        const [tasksRes, notesRes] = await Promise.all([
          api.get(`/negotiation/${selectedNegotiation.id}/tasks`),
          api.get(`/negotiation/${selectedNegotiation.id}/notes`)
        ]);
        setNegotiationTasks(tasksRes.data || []);
        setNegotiationNotes(notesRes.data || []);
      } catch (error) {
        console.error('Error loading negotiation details:', error);
        setNegotiationTasks([]);
        setNegotiationNotes([]);
      } finally {
        setLoadingDetails(false);
      }
    };
    fetchNegotiationDetails();
  }, [selectedNegotiation]);

  const updateUrlState = (nextCustomerId, nextTab, replace = true) => {
    const params = new URLSearchParams();
    if (nextCustomerId) params.set('customer_id', String(nextCustomerId));
    if (nextTab && validTabValues.has(nextTab)) params.set('tab', nextTab);
    const query = params.toString();
    navigate(query ? `/customer/cumulativa?${query}` : '/customer/cumulativa', { replace });
  };
  const toggleSection = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedCustomerOption = useMemo(() => {
    if (!selectedCustomerId) return null;
    return customerOptions.find((opt) => String(opt.id) === String(selectedCustomerId)) || selectedCustomer || null;
  }, [customerOptions, selectedCustomer, selectedCustomerId]);

  const dueTasks = useMemo(() => {
    return negotiationTasks
      .filter((t) => t.data_scadenza)
      .slice()
      .sort((a, b) => new Date(a.data_scadenza) - new Date(b.data_scadenza))
      .slice(0, 6);
  }, [negotiationTasks]);

  const openNegotiations = useMemo(
    () => negotiations.filter((n) => !['closed', 'complet'].some((k) => (n.status || '').toLowerCase().includes(k))),
    [negotiations]
  );

  const rateizzazioni = useMemo(
    () => negotiations.filter((n) => (n.category_name || '').toLowerCase().includes('rate')),
    [negotiations]
  );

  return (
    <Box sx={{ p: 2.5, bgcolor: palette.bg, minHeight: '100vh', fontFamily: pageFont }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ color: palette.muted, fontSize: 12, mb: 0.25 }}>Clienti &gt; Dettaglio cliente</Typography>
          <Typography sx={{ fontWeight: 700, color: palette.title, fontSize: 21 }}>Dettaglio Cliente</Typography>
        </Box>
        <Autocomplete
          options={customerOptions}
          loading={loadingOptions}
          value={selectedCustomerOption}
          getOptionLabel={(opt) =>
            `${opt?.surname || ''} ${opt?.name || ''}`.trim() || opt?.ragione_sociale || `ID ${opt?.id || ''}`
          }
          onInputChange={(_, value) => setSearchText(value || '')}
          onChange={(_, value) => {
            const nextId = value?.id ? String(value.id) : '';
            setSelectedCustomerId(nextId);
            updateUrlState(nextId, activeTab, false);
          }}
          sx={{ minWidth: { xs: '100%', lg: 360 } }}
          isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
          renderInput={(params) => <TextField {...params} label="Seleziona cliente" size="small" />}
        />
      </Stack>

      {!selectedCustomerId && <Alert severity="info">Seleziona un cliente per visualizzare la dashboard.</Alert>}

      {selectedCustomerId && loadingData && (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <LoaderProgress size={56} />
        </Box>
      )}

      {selectedCustomer && !loadingData && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={3}>
            <Stack spacing={2}>
              <Paper sx={{ ...cardSx, p: 1.75 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Cliente</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Button size="small" sx={{ textTransform: 'none' }} onClick={() => navigate(`/customer/${selectedCustomer.id}`)}>
                      Modifica
                    </Button>
                    <IconButton size="small" onClick={() => toggleSection('leftCustomer')}>
                      {collapsed.leftCustomer ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </IconButton>
                  </Stack>
                </Stack>
                <Collapse in={!collapsed.leftCustomer}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(palette.primary, 0.15), color: palette.primary }}>
                      {getInitials(selectedCustomer)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 16, color: palette.title }}>
                        {selectedCustomer.name} {selectedCustomer.surname}
                      </Typography>
                      <Chip label="Cliente attivo" color="success" size="small" variant="outlined" />
                    </Box>
                  </Stack>
                  <Stack spacing={0.8}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Mail size={13} color={palette.muted} />
                      <Typography sx={{ fontSize: 12 }}>{selectedCustomer.email || '-'}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Phone size={13} color={palette.muted} />
                      <Typography sx={{ fontSize: 12 }}>{selectedCustomer.telephone || '-'}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MapPin size={13} color={palette.muted} />
                      <Typography sx={{ fontSize: 12 }}>
                        {selectedCustomer.strada_residenza || '-'} {selectedCustomer.citta_residenza || ''}
                      </Typography>
                    </Stack>
                  </Stack>
                </Collapse>
              </Paper>

              <Paper sx={{ ...cardSx, p: 1.75 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Fascicolo</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {selectedDossier && (
                      <Button size="small" sx={{ textTransform: 'none' }} onClick={() => navigate(`/dossier/${selectedDossier.id}`)}>
                        Modifica
                      </Button>
                    )}
                    <IconButton size="small" onClick={() => toggleSection('leftDossier')}>
                      {collapsed.leftDossier ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </IconButton>
                  </Stack>
                </Stack>
                <Collapse in={!collapsed.leftDossier}>
                  {selectedDossier ? (
                    <Stack spacing={0.7}>
                      <Typography sx={{ fontSize: 12, color: palette.muted }}>Codice</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{selectedDossier.reference || `#${selectedDossier.id}`}</Typography>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography sx={{ fontSize: 12, color: palette.muted }}>Stato</Typography>
                      <Chip size="small" label={selectedDossier.status || '-'} color={statusToColor(selectedDossier.status)} sx={{ width: 'fit-content' }} />
                      <Typography sx={{ fontSize: 12, color: palette.muted, mt: 1 }}>Creato il</Typography>
                      <Typography sx={{ fontSize: 13 }}>{formatDate(selectedDossier.created_at || selectedDossier.data_conferimento)}</Typography>
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: palette.muted }}>Nessun fascicolo disponibile.</Typography>
                  )}
                </Collapse>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Paper sx={{ ...cardSx, p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Area Operativa</Typography>
                <IconButton size="small" onClick={() => toggleSection('center')}>
                  {collapsed.center ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </IconButton>
              </Stack>
              <Collapse in={!collapsed.center}>
                <Tabs
                  value={activeTab}
                  onChange={(_, value) => {
                    setActiveTab(value);
                    updateUrlState(selectedCustomerId, value, true);
                  }}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 40,
                    mb: 1,
                    '& .MuiTabs-indicator': { height: 2, backgroundColor: palette.primary },
                    '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: 12, fontWeight: 600, color: palette.muted },
                    '& .Mui-selected': { color: palette.primary }
                  }}
                >
                  {tabs.map((t) => (
                    <Tab key={t.value} value={t.value} icon={<t.icon size={14} />} iconPosition="start" label={t.label} sx={{ gap: 0.75 }} />
                  ))}
                </Tabs>

              {activeTab === 'attivita' && (
                <Box sx={tabScrollableSx}>
                  <Stack spacing={1.2}>
                    <Paper sx={{ border: `1px dashed ${palette.stroke}`, borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fbfdff' }}>
                      <StickyNote size={26} color={palette.muted} style={{ marginBottom: 6 }} />
                      <Typography sx={{ fontWeight: 700, color: palette.title }}>Nessuna Nota Appuntata</Typography>
                      <Typography sx={{ fontSize: 12, color: palette.muted }}>
                        Le note importanti verranno mostrate qui per un accesso rapido.
                      </Typography>
                    </Paper>

                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: palette.title }}>
                      {new Date().toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                    </Typography>

                    {loadingDetails ? (
                      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                        <LoaderProgress size={32} />
                      </Box>
                    ) : (
                      <TableContainer sx={{ border: `1px solid ${palette.stroke}`, borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Task</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Priorita</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Scade il</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12, width: 40 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dueTasks.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                Nessun task disponibile
                              </TableCell>
                            </TableRow>
                          )}
                          {dueTasks.map((task) => (
                            <TableRow key={task.id} hover>
                              <TableCell>
                                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{getTaskTitle(task)}</Typography>
                                <Typography sx={{ fontSize: 11, color: palette.muted }}>{getTaskAssignee(task)}</Typography>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const priorityLabel = getTaskPriorityLabel(task);
                                  const isHigh = String(priorityLabel).toLowerCase().includes('alta');
                                  return (
                                <Chip
                                  size="small"
                                      label={priorityLabel}
                                      color={isHigh ? 'error' : 'warning'}
                                  variant="outlined"
                                />
                                  );
                                })()}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{formatDate(task.data_scadenza)}</TableCell>
                              <TableCell align="right">
                                <IconButton size="small">
                                  <MoreVertical size={14} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </TableContainer>
                    )}

                    <Button startIcon={<Plus size={14} />} size="small" sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                      Nuovo task
                    </Button>
                  </Stack>
                </Box>
              )}

              {activeTab === 'task' && (
                loadingDetails ? (
                  <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                    <LoaderProgress size={32} />
                  </Box>
                ) : (
                  <Box sx={tabScrollableSx}>
                    <List dense>
                      {negotiationTasks.length === 0 && <ListItem><ListItemText primary="Nessun task" /></ListItem>}
                      {negotiationTasks.map((task) => (
                        <ListItem key={task.id} secondaryAction={<Chip label={formatDate(task.data_scadenza)} size="small" />}>
                          <ListItemText
                            primary={getTaskTitle(task)}
                            secondary={`${getTaskAssignee(task)} · ${task.status || (String(task.eseguito) === '1' ? 'Completato' : 'Da fare')}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )
              )}

              {activeTab === 'note' && (
                loadingDetails ? (
                  <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                    <LoaderProgress size={32} />
                  </Box>
                ) : (
                  <Box sx={tabScrollableSx}>
                    <Stack spacing={1.2}>
                      {negotiationNotes.length === 0 && (
                        <Paper sx={{ p: 1.5, border: `1px dashed ${palette.stroke}`, borderRadius: 2 }}>
                          <Typography sx={{ fontSize: 13, color: palette.muted }}>Nessuna nota</Typography>
                        </Paper>
                      )}
                      {negotiationNotes.map((note) => (
                        <Paper
                          key={note.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${palette.stroke}`,
                            backgroundColor: '#fbfdff'
                          }}
                        >
                          <Typography sx={{ fontSize: 14, color: palette.title, whiteSpace: 'pre-line', lineHeight: 1.45 }}>
                            {getNoteText(note)}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: palette.muted, mt: 1 }}>
                            {formatDate(note.created_at)} · {getNoteAuthor(note)}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )
              )}

              {activeTab === 'solleciti' && (
                <Box sx={tabScrollableSx}>
                  <List dense>
                    {openNegotiations.slice(0, 8).map((n) => (
                      <ListItem key={n.id} secondaryAction={<Chip label={n.status || '-'} size="small" color={statusToColor(n.status)} />}>
                        <ListItemText primary={n.reference || `Pratica #${n.id}`} secondary={`Scadenza: ${formatDate(n.data_decadenza)}`} />
                      </ListItem>
                    ))}
                    {openNegotiations.length === 0 && <ListItem><ListItemText primary="Nessun sollecito disponibile" /></ListItem>}
                  </List>
                </Box>
              )}

                {activeTab === 'contatti' && (
                <Box sx={tabScrollableSx}>
                  <Stack spacing={1.2}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Contatti Cliente</Typography>
                    <Typography sx={{ fontSize: 12 }}>Email: {selectedCustomer.email || '-'}</Typography>
                    <Typography sx={{ fontSize: 12 }}>Telefono: {selectedCustomer.telephone || '-'}</Typography>
                    <Typography sx={{ fontSize: 12 }}>Azienda: {selectedCustomer.ragione_sociale || '-'}</Typography>
                    <Divider />
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Ultime interazioni</Typography>
                    {negotiationNotes.slice(0, 3).map((note) => (
                      <Typography key={note.id} sx={{ fontSize: 12, color: palette.text }}>
                        • {getNoteText(note)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
              </Collapse>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={3}>
            <Stack spacing={1.5}>
              <Paper sx={{ ...cardSx, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Fascicoli</Typography>
                  <IconButton size="small" onClick={() => toggleSection('rightFascicoli')}>
                    {collapsed.rightFascicoli ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </IconButton>
                </Stack>
                <Collapse in={!collapsed.rightFascicoli}>
                  {selectedDossier ? (
                    <>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{selectedDossier.reference || `#${selectedDossier.id}`}</Typography>
                        <Chip label={selectedDossier.status || '-'} size="small" color={statusToColor(selectedDossier.status)} />
                      </Stack>
                      <Typography sx={{ fontSize: 12, color: palette.muted }}>Scadenza: {formatDate(selectedDossier.data_fine || selectedDossier.updated_at)}</Typography>
                      <Button size="small" endIcon={<ChevronRight size={14} />} sx={{ mt: 0.5, textTransform: 'none' }}>
                        Vai al fascicolo
                      </Button>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: palette.muted }}>Nessun fascicolo disponibile.</Typography>
                  )}
                </Collapse>
              </Paper>

              <Paper sx={{ ...cardSx, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Pratiche ({negotiations.length})</Typography>
                  <IconButton size="small" onClick={() => toggleSection('rightPratiche')}>
                    {collapsed.rightPratiche ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </IconButton>
                </Stack>
                <Collapse in={!collapsed.rightPratiche}>
                  <Stack spacing={0.8}>
                    {negotiations.slice(0, 3).map((n) => (
                      <Stack
                        key={n.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ p: 1, border: `1px solid ${palette.stroke}`, borderRadius: 1.5, cursor: 'pointer' }}
                        onClick={() => setSelectedNegotiation(n)}
                      >
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{n.reference || `#${n.id}`}</Typography>
                          <Typography sx={{ fontSize: 11, color: palette.muted }}>{n.category_name || '-'}</Typography>
                        </Box>
                        <Chip label={n.status || '-'} size="small" color={statusToColor(n.status)} />
                      </Stack>
                    ))}
                  </Stack>
                  <Button size="small" sx={{ mt: 0.8, textTransform: 'none' }}>
                    Vedi tutte le pratiche
                  </Button>
                </Collapse>
              </Paper>

              <Paper sx={{ ...cardSx, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Rateizzazioni ({rateizzazioni.length})</Typography>
                  <IconButton size="small" onClick={() => toggleSection('rightRateizzazioni')}>
                    {collapsed.rightRateizzazioni ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </IconButton>
                </Stack>
                <Collapse in={!collapsed.rightRateizzazioni}>
                  {rateizzazioni.slice(0, 2).map((r) => (
                    <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.7 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{r.reference || `#${r.id}`}</Typography>
                      <Chip size="small" label={r.status || '-'} color={statusToColor(r.status)} />
                    </Stack>
                  ))}
                  {rateizzazioni.length === 0 && <Typography sx={{ fontSize: 12, color: palette.muted }}>Nessuna rateizzazione.</Typography>}
                </Collapse>
              </Paper>

              <Paper sx={{ ...cardSx, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Allegati</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Button size="small" startIcon={<Plus size={13} />} sx={{ textTransform: 'none' }}>
                      Aggiungi
                    </Button>
                    <IconButton size="small" onClick={() => toggleSection('rightAllegati')}>
                      {collapsed.rightAllegati ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </IconButton>
                  </Stack>
                </Stack>
                <Collapse in={!collapsed.rightAllegati}>
                  <Typography sx={{ fontSize: 12, color: palette.muted }}>3 allegati presenti</Typography>
                </Collapse>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
